import { useEffect } from 'react'

const CARD = '.ant-card'

/**
 * Cards below the fold rise into view as they scroll in, as the website's
 * SlideUp / ScrollYProgress reveals do. Cards already on screen keep their
 * entrance stagger (motion.css) instead.
 *
 * Works on every page without touching it: it watches the content column for
 * cards (they often arrive after the page, when data loads), and marks the
 * ones that start below the fold with `data-fh-reveal="waiting"` until they
 * are a little way into view. Off for reduced motion.
 */
export function ScrollReveal() {
  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return
    const content = document.querySelector('.fh-content')
    if (!content || typeof IntersectionObserver === 'undefined') return

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue
          entry.target.setAttribute('data-fh-reveal', 'shown')
          observer.unobserve(entry.target)
        }
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.1 },
    )

    const consider = (card: Element) => {
      if (card.hasAttribute('data-fh-reveal')) return
      // A card inside a card moves with its parent.
      if (card.parentElement?.closest(CARD)) return
      if (card.getBoundingClientRect().top < window.innerHeight) return
      card.setAttribute('data-fh-reveal', 'waiting')
      observer.observe(card)
    }

    const scan = (root: Element) => {
      if (root.matches(CARD)) consider(root)
      root.querySelectorAll(CARD).forEach(consider)
    }

    scan(content)
    const mutations = new MutationObserver((records) => {
      for (const record of records) {
        record.addedNodes.forEach((node) => {
          if (node instanceof Element) scan(node)
        })
      }
    })
    mutations.observe(content, { childList: true, subtree: true })

    return () => {
      mutations.disconnect()
      observer.disconnect()
    }
  }, [])

  return null
}
